import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/shared/card';
import { Alert, AlertDescription } from "@/components/shared/alert";
import MainLayout from '@/components/layout/MainLayout';
import { ROUTES } from '@/config/routes';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, status } = useAuth();
  const [sessionEstablished, setSessionEstablished] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  // Check for recovery parameters in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.substring(1));
    
    // Check for both 'code' and 'token' parameters (Supabase uses 'token' for PKCE flows)
    const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
    const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
    const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                           (hashParams.has('type') && hashParams.get('type') === 'recovery');
    
    const isRecoveryFlow = hasRecoveryCode || hasRecoveryToken || hasRecoveryType;
    
    console.log('[ResetPassword] Recovery detection:', { 
      hasRecoveryCode, 
      hasRecoveryToken, 
      hasRecoveryType, 
      isRecoveryFlow,
      searchParams: Array.from(urlParams.keys()),
      hashParams: Array.from(hashParams.keys())
    });
    
    if (!isRecoveryFlow) {
      console.log('[ResetPassword] No recovery parameters found - redirecting to login');
      navigate(ROUTES.LOGIN, { 
        state: { 
          error: 'Invalid password reset link. Please request a new one.',
          fromPasswordReset: true 
        } 
      });
      return;
    }
    
    // If we have recovery parameters, try to establish a session
    const establishSession = async () => {
      try {
        setIsProcessing(true);
        setMessage('Establishing secure session...');
        
        // Try to get the current session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[ResetPassword] Error getting session:', error);
          setMessage('Error establishing session. Please try again.');
          setIsProcessing(false);
          return;
        }
        
        if (session) {
          console.log('[ResetPassword] Session already established:', session.user?.id);
          setSessionEstablished(true);
          setIsProcessing(false);
          setMessage('Session established successfully. You can now set your new password.');
        } else {
          console.log('[ResetPassword] No session found - waiting for auth events');
          setIsProcessing(false);
          setMessage('Establishing secure session...');
        }
      } catch (error) {
        console.error('[ResetPassword] Exception establishing session:', error);
        setMessage('Error establishing session. Please try again.');
        setIsProcessing(false);
      }
    };
    
    establishSession();
  }, [location, navigate]);

  // Listen for auth state changes from AuthContext
  useEffect(() => {
    if (status === 'authenticated' && user) {
      console.log('[ResetPassword] User authenticated via AuthContext:', user.id);
      setSessionEstablished(true);
      setIsProcessing(false);
      
      // Check if this is a password update (user just changed their password)
      const urlParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const hasRecoveryCode = urlParams.has('code') || hashParams.has('code');
      const hasRecoveryToken = urlParams.has('token') || hashParams.has('token');
      const hasRecoveryType = (urlParams.has('type') && urlParams.get('type') === 'recovery') || 
                             (hashParams.has('type') && hashParams.get('type') === 'recovery');
      
      if (hasRecoveryCode || hasRecoveryToken || hasRecoveryType) {
        // This is likely a password update
        toast.success('Password updated successfully!');
        setMessage('Password updated successfully! Redirecting to benchmarking...');
        setTimeout(() => navigate(ROUTES.PERMIT_BENCHMARKING.ROOT), 2000);
      } else {
        // This is a normal session establishment
        setMessage('Session established successfully. You can now set your new password.');
      }
    } else if (status === 'idle' && !isProcessing) {
      // User signed out or session expired
      console.log('[ResetPassword] User signed out or session expired');
      setSessionEstablished(false);
      setMessage('Session expired. Please try the password reset link again.');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate(ROUTES.LOGIN, { 
          state: { 
            error: 'Session expired. Please try the password reset link again.',
            fromPasswordReset: true 
          } 
        });
      }, 3000);
    }
  }, [status, user, navigate, location.search, location.hash, isProcessing]);

  return (
    <MainLayout>
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-foreground text-center">
            Reset your password
          </h2>
          <Card className="mt-8 p-6 bg-card">
            {message && (
              <Alert className="mb-6" role="status" aria-live="polite">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {isProcessing && (
              <Alert className="mb-6" role="status" aria-live="polite">
                <AlertDescription>Processing...</AlertDescription>
              </Alert>
            )}
            {!sessionEstablished && !isProcessing && (
              <Alert className="mb-6" role="status" aria-live="polite">
                <AlertDescription>Establishing secure session...</AlertDescription>
              </Alert>
            )}
            {sessionEstablished && (
              <Auth
                supabaseClient={supabase}
                appearance={{ 
                  theme: ThemeSupa,
                  style: {
                    button: {
                      background: 'var(--primary)',
                      color: 'var(--primary-foreground)',
                      borderRadius: '6px',
                      fontWeight: '500'
                    },
                    anchor: {
                      color: 'var(--primary)'
                    }
                  }
                }}
                theme="light"
                providers={[]}
                view="update_password"
              />
            )}
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ResetPassword;